/**
 * HALO - Dashboard Home Page
 * Landing page with KPI cards, wellness trend chart, and recent activity.
 * Now connected to live Supabase data via useDashboardStats hook.
 */

import { motion } from "framer-motion";
import {
  Users,
  Activity,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Bell,
  Clock,
  Shield,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SectionCards } from "@/components/dashboard/SectionCards";
import { WellnessChart } from "@/components/dashboard/WellnessChart";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useState } from "react";

// ============================================================================
// COMPONENT
// ============================================================================

interface DashboardHomeProps {
  organizationId: string;
  userId: string;
}

export default function DashboardHome({ organizationId }: DashboardHomeProps) {
  const navigate = useNavigate();
  const [chartDays, setChartDays] = useState(30);

  const { kpis, chartData, recentActivity, isLoading, lastUpdated, refresh } =
    useDashboardStats(organizationId, chartDays);

  // Build KPI cards from live data
  const kpiCards = [
    {
      title: "Total Employees",
      value: isLoading ? "—" : kpis.totalEmployees.toString(),
      description: isLoading
        ? "Loading..."
        : `${kpis.activeNow} currently active`,
      footer: isLoading
        ? "Fetching data..."
        : `${kpis.totalEmployees - kpis.activeNow} offline`,
      icon: <Users className="w-4 h-4 text-signal-blue" />,
    },
    {
      title: "Active Now",
      value: isLoading ? "—" : kpis.activeNow.toString(),
      description: isLoading
        ? "Loading..."
        : kpis.totalEmployees > 0
        ? `${Math.round((kpis.activeNow / kpis.totalEmployees) * 100)}% of team`
        : "No employees yet",
      footer: "Monitoring in real-time",
      icon: <Activity className="w-4 h-4 text-signal-green" />,
    },
    {
      title: "Avg Wellness",
      value: isLoading
        ? "—"
        : (kpis.avgWellnessScore ?? 0) > 0
        ? `${kpis.avgWellnessScore}%`
        : "—",
      description: isLoading
        ? "Loading..."
        : (kpis.avgWellnessScore ?? 0) >= 75
        ? "Team is doing well"
        : (kpis.avgWellnessScore ?? 0) >= 50
        ? "Moderate — check alerts"
        : (kpis.avgWellnessScore ?? 0) > 0
        ? "Needs attention"
        : "No data yet",
      footer: isLoading
        ? "Fetching data..."
        : `Posture: ${kpis.avgPosture}% · Fatigue: ${Math.round(
            (kpis.avgFatigue ?? 0) * 100,
          )}%`,
      icon: <TrendingUp className="w-4 h-4 text-primary" />,
      ...((kpis.avgWellnessScore ?? 0) > 0 && {
        trend: {
          value: `${kpis.avgWellnessScore}%`,
          direction:
            (kpis.avgWellnessScore ?? 0) >= 70 ? ("up" as const) : ("down" as const),
          isPositive: (kpis.avgWellnessScore ?? 0) >= 70,
        },
      }),
    },
    {
      title: "Active Alerts",
      value: isLoading ? "—" : kpis.activeAlerts.toString(),
      description: isLoading
        ? "Loading..."
        : kpis.activeAlerts === 0
        ? "No active alerts"
        : kpis.highFatigueCount > 0
        ? `${kpis.highFatigueCount} high fatigue`
        : `${kpis.poorPostureCount} poor posture`,
      footer: isLoading
        ? "Fetching data..."
        : kpis.alertsTrend !== 0
        ? `${kpis.alertsTrend > 0 ? "+" : ""}${kpis.alertsTrend}% vs yesterday`
        : "Compared to yesterday",
      icon: <AlertTriangle className="w-4 h-4 text-signal-amber" />,
      ...(kpis.activeAlerts > 0 && {
        trend: {
          value: kpis.activeAlerts.toString(),
          direction: "up" as const,
          isPositive: false,
        },
      }),
    },
  ];

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      {/* Last updated indicator */}
      {lastUpdated && (
        <div className="flex items-center justify-end gap-2 px-4 lg:px-6">
          <button
            onClick={refresh}
            className="flex items-center gap-1.5 text-[11px] text-muted-slate hover:text-foreground transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Updated{" "}
            {lastUpdated.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
            })}
          </button>
        </div>
      )}

      {/* KPI Section Cards */}
      <SectionCards cards={kpiCards} />

      {/* Chart + Activity side-by-side on large screens */}
      <div className="grid grid-cols-1 gap-4 px-4 lg:grid-cols-3 lg:px-6">
        {/* Wellness chart - takes 2/3 width */}
        <div className="lg:col-span-2">
          <WellnessChart data={chartData} onTimeRangeChange={setChartDays} />
        </div>

        {/* Recent activity - takes 1/3 width */}
        <Card className="flex flex-col">
          <CardHeader>
            <div>
              <CardTitle className="text-base">Recent Activity</CardTitle>
              <CardDescription>Latest wellness events</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="flex-1">
            {recentActivity.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <Bell className="w-8 h-8 text-muted-slate/30 mb-3" />
                <p className="text-sm text-muted-slate">No recent activity</p>
                <p className="text-xs text-muted-slate/60 mt-1">
                  Activity will appear here when team members are active
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((activity, idx) => (
                  <motion.div
                    key={activity.id}
                    className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-surface-elevated transition-colors"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <div className="shrink-0 mt-0.5">
                      {activity.severity === "destructive" ? (
                        <div className="w-7 h-7 rounded-lg bg-signal-crimson/10 flex items-center justify-center">
                          <AlertTriangle className="w-3.5 h-3.5 text-signal-crimson" />
                        </div>
                      ) : activity.severity === "warning" ? (
                        <div className="w-7 h-7 rounded-lg bg-signal-amber/10 flex items-center justify-center">
                          <Bell className="w-3.5 h-3.5 text-signal-amber" />
                        </div>
                      ) : activity.severity === "info" ? (
                        <div className="w-7 h-7 rounded-lg bg-shield/10 flex items-center justify-center">
                          <Shield className="w-3.5 h-3.5 text-shield" />
                        </div>
                      ) : (
                        <div className="w-7 h-7 rounded-lg bg-signal-green/10 flex items-center justify-center">
                          <Activity className="w-3.5 h-3.5 text-signal-green" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground leading-snug">
                        {activity.message}
                      </p>
                      <p className="text-[11px] text-muted-slate mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {activity.time}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>

          {/* View all link */}
          <div className="px-6 pb-4">
            <button
              onClick={() => navigate("/team-pulse")}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary/10 rounded-xl transition-colors font-medium"
            >
              View Team Pulse
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 gap-4 px-4 sm:grid-cols-3 lg:px-6">
        <motion.button
          onClick={() => navigate("/team-pulse")}
          className="halo-card flex items-center gap-4 text-left hover:border-signal-blue transition-colors"
          whileHover={{ y: -2 }}
        >
          <div className="w-10 h-10 rounded-xl bg-signal-blue/10 flex items-center justify-center shrink-0">
            <Activity className="w-5 h-5 text-signal-blue" />
          </div>
          <div>
            <p className="font-medium text-foreground">Team Pulse</p>
            <p className="text-xs text-muted-slate">
              Real-time wellness monitoring
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-slate ml-auto" />
        </motion.button>

        <motion.button
          onClick={() => navigate("/admin")}
          className="halo-card flex items-center gap-4 text-left hover:border-primary transition-colors"
          whileHover={{ y: -2 }}
        >
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-medium text-foreground">Manage Team</p>
            <p className="text-xs text-muted-slate">Add and manage employees</p>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-slate ml-auto" />
        </motion.button>

        <motion.div className="halo-card flex items-center gap-4 text-left opacity-50 cursor-not-allowed">
          <div className="w-10 h-10 rounded-xl bg-signal-green/10 flex items-center justify-center shrink-0">
            <TrendingUp className="w-5 h-5 text-signal-green" />
          </div>
          <div>
            <p className="font-medium text-foreground">AI Analysis</p>
            <p className="text-xs text-muted-slate">Coming soon</p>
          </div>
          <Badge variant="outline" className="ml-auto text-[10px]">
            Soon
          </Badge>
        </motion.div>
      </div>
    </div>
  );
}
